import React, { useCallback, useState } from 'react'
import Fuse from 'fuse.js'
import { useQuery } from '@tanstack/react-query'
import { isUserAdminInSociety } from '../../../../helpers/society'
import { ContactList } from '../../../../components/society/ContactList'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { ButtonVariant } from '../../../../components/common/Button'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { CreateUpdateContactModal } from '../../../../components/society/Contact/CreateUpdateContactModal'
import { useModal } from '../../../../hooks/useModal'
import { ViewBase } from '../../../../components/common/ViewBase'
import { ErrorBoundary } from '../../../../components/common/ErrorBoundary'
import { IllustrationChoices } from '../../../../components/common/Illustration'
import { useDocumentTitle } from '../../../../hooks/useDocumentTitle'
import { reverseDocumentTitle } from '../../../../navigation/reverseDocumentTitle'
import { useAuth } from '../../../../hooks/useAuth'
import { boardRoomContactQueries } from '../../../../api/society-boardroom-contacts'
import { i18nSocietyType } from '../../../../i18n'

export const BoardContactsView = (): React.ReactNode => {
  const { userId } = useAuth()
  const { translate } = useAppTranslation()
  const [searchString, setSearchString] = useState('')
  const { society } = useCurrentSociety()
  useDocumentTitle(
    reverseDocumentTitle('management:contacts', {
      '{{ societyName }}': society?.name,
    })
  )
  const {
    show: showCreateModal,
    open: openCreateModal,
    close: closeCreateModal,
  } = useModal()

  const isAdmin = isUserAdminInSociety(society, userId as string)

  const { data: contacts, isLoading: isLoadingBoardRoomContacts } = useQuery(
    boardRoomContactQueries.getContactsPerSociety(society?._id)
  )

  const onChangeSearchString = (
    event: React.FormEvent<HTMLInputElement>
  ): void => {
    setSearchString(event.currentTarget.value)
  }

  const getFilteredContacts = useCallback(() => {
    const options = {
      threshold: 0.2,
      keys: ['name', 'title'],
    }
    const _contacts = contacts || []

    const fuse = new Fuse(_contacts, options)

    return searchString === ''
      ? _contacts
      : fuse.search(searchString).map((result) => result.item)
  }, [contacts, searchString])

  const filteredContacts = getFilteredContacts()

  const loading = isLoadingBoardRoomContacts

  const buttons = isAdmin
    ? [
        {
          variant: ButtonVariant.PRIMARY,
          onClick: openCreateModal,
          label: translate('common.actions.add'),
        },
      ]
    : []

  const errorView =
    !loading && contacts && contacts.length === 0
      ? {
          title: translate('contactsView.emptyState.title', {
            context: i18nSocietyType(society?.societyTypeDisplayName),
          }),
          subtitle: translate('contactsView.emptyState.subtitle', {
            context: i18nSocietyType(society?.societyTypeDisplayName),
          }),
          illustration: IllustrationChoices.EMPTY,
        }
      : undefined

  const noSearchResultErrorView =
    !loading && filteredContacts.length === 0 && searchString !== ''
      ? {
          title: translate('contactsView.emptyState.noSearchResultsTitle'),
          subtitle: translate(
            'contactsView.emptyState.noSearchResultsSubtitle'
          ),
          illustration: IllustrationChoices.EMPTY,
        }
      : undefined

  return (
    <ErrorBoundary>
      <ViewBase
        title={translate('boardContactsView.title', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })}
        buttons={buttons}
        searchBar={{
          value: searchString,
          onChange: onChangeSearchString,
        }}
        loading={loading}
        errorView={errorView}
        noSearchResultErrorView={noSearchResultErrorView}
      >
        <ContactList contacts={getFilteredContacts()} management />
      </ViewBase>
      {isAdmin && (
        <CreateUpdateContactModal
          management
          show={showCreateModal}
          close={closeCreateModal}
        />
      )}
    </ErrorBoundary>
  )
}
